import { TrainingExerciseRequest, TrainingRequest, EnduranceTrainingRequest } from "../../../../shared/requests/trainingPlan.request";
import { TrainingExerciseDto } from "../../../../shared/dtos/trainingExercise.dto";
import { TrainingDto, EnduranceTrainingDto } from "../../../../shared/dtos/trainingplan.dto";
import { ExerciseDto } from "../../../../shared/dtos/exercise.dto";
import { EnduranceTrainingTypeDto } from "../../../../shared/dtos/enduranceTrainingType.dto";
import { ExerciseType, TrainingType } from "../../../../shared/utils/enums";

export type TrainingPlanFormValues = {
  trainings: Array<TrainingFormValues>;
};

export type TrainingFormValues = {
  name: string;
  description?: string;
  workoutsPerWeek?: number;
  workoutPause?: number;
  exercises: Array<ExerciseFormValue>;
  trainingKind: TrainingType;
  enduranceTrainingType?: EnduranceTrainingTypeDto;
}

export type ExerciseUnit = 'sec' | 'reps' | 'min';

export type ExerciseFormValue = {
  name: string;
  description: string;
  sets?: number;
  skipPause: boolean;
  needsVideoFeedback: boolean;
  useCustomDescription: boolean;
  exerciseType: ExerciseType;
  exercise?: ExerciseDto;
  time?: number;
  repetition?: number;
  unit?: ExerciseUnit;
};

export const isSimpleExerciseType = (exerciseType: ExerciseType): boolean =>
  exerciseType === ExerciseType.TEACHING || exerciseType === ExerciseType.RELAXATION;

export const isPauseSkippedByDefault = (exerciseType: ExerciseType): boolean =>
  isSimpleExerciseType(exerciseType);

const defaultExerciseUnits: { [key in ExerciseType]: ExerciseUnit | undefined } = {
  [ExerciseType.STATIC]: 'sec',
  [ExerciseType.DYNAMIC]: 'reps',
  [ExerciseType.ENDURANCE]: 'min',
  [ExerciseType.TEACHING]: undefined,
  [ExerciseType.RELAXATION]: undefined,
};

export const toDefaultExerciseUnit = (exerciseType: ExerciseType): ExerciseUnit | undefined =>
  defaultExerciseUnits[exerciseType];

const toPositiveOrUndefined = (value?: number): number | undefined =>
  value !== undefined && value > 0 ? value : undefined;

export const toExerciseFormValue = (dto: TrainingExerciseDto): ExerciseFormValue => ({
  name: dto.name,
  description: dto.description,
  sets: dto.sets,
  skipPause: dto.skipPause,
  needsVideoFeedback: dto.needsVideoFeedback,
  useCustomDescription: dto.useCustomDescription,
  exercise: dto.exercise,
  exerciseType: dto.exerciseType,
  time: isSimpleExerciseType(dto.exerciseType) ? undefined : dto.time,
  repetition: isSimpleExerciseType(dto.exerciseType) ? undefined : dto.repetition,
  unit: isSimpleExerciseType(dto.exerciseType) ? undefined : toExerciseUnit(dto.unit),
});

const toExerciseUnit = (unit?: string): ExerciseUnit | undefined => {
  if (unit === 'min' || unit === 'reps' || unit === 'sec') {
    return unit;
  }

  return undefined;
};

export const toTrainingFormValues = (dto: TrainingDto): TrainingFormValues => ({
  name: dto.name,
  workoutsPerWeek: dto.workoutsPerWeek,
  workoutPause: dto.workoutPause,
  exercises: (dto.exercises ?? []).map(exercise => toExerciseFormValue(exercise)),
  trainingKind: TrainingType.STRENGTH,
  enduranceTrainingType: undefined,
});

export const toEnduranceTrainingFormValues = (dto: EnduranceTrainingDto): TrainingFormValues => ({
  name: dto.name,
  description: dto.description,
  workoutsPerWeek: dto.workoutsPerWeek,
  workoutPause: 60,
  exercises: [],
  trainingKind: TrainingType.ENDURANCE,
  enduranceTrainingType: dto.enduranceTrainingType,
});

export const toTrainingExerciseRequest = (formValue: ExerciseFormValue, index: number): TrainingExerciseRequest => ({
  name: formValue.name,
  description: formValue.description,
  sets: formValue.sets ?? 0,
  skipPause: formValue.skipPause ?? false,
  needsVideoFeedback: formValue.needsVideoFeedback ?? false,
  useCustomDescription: formValue.useCustomDescription ?? false,
  exerciseId: formValue.exercise?.id,
  exerciseType: formValue.exerciseType,
  time: toPositiveOrUndefined(formValue.time),
  repetition: toPositiveOrUndefined(formValue.repetition),
  unit: formValue.unit,
  order: index,
});

export const toTrainingRequest = (training: TrainingFormValues, index: number): TrainingRequest => ({
  name: training.name,
  workoutsPerWeek: training.workoutsPerWeek ?? 0,
  workoutPause: training.workoutPause ?? 0,
  exercises: training.exercises.map((exercise, exerciseIndex) => toTrainingExerciseRequest(exercise, exerciseIndex)),
  order: index,
});

export const toEnduranceTrainingRequest = (training: TrainingFormValues, index: number): EnduranceTrainingRequest => ({
  type: TrainingType.ENDURANCE,
  name: training.name,
  description: training.description,
  order: index,
  workoutsPerWeek: training.workoutsPerWeek ?? 0,
  enduranceTrainingTypeId: training.enduranceTrainingType!.id,
});

export const splitTrainingRequests = (
  trainings: TrainingFormValues[]
): { trainings: TrainingRequest[]; enduranceTrainings: EnduranceTrainingRequest[] } => {
  const normal: TrainingRequest[] = [];
  const endurance: EnduranceTrainingRequest[] = [];

  trainings.forEach((training, index) => {
    if (training.trainingKind === TrainingType.ENDURANCE) {
      endurance.push(toEnduranceTrainingRequest(training, index));
    } else {
      normal.push(toTrainingRequest(training, index));
    }
  });

  return { trainings: normal, enduranceTrainings: endurance };
};
